import React, { FC, memo, useCallback, useState } from 'react';
import { Icon, Steps, Timeline, message, Tooltip } from '@ss/mtd-react';
import useGo from '@utils/navigate';
import dayjs from 'dayjs';
import { IVolunteerRecord, EnumVolunteerItemStatus, EnumVolunteerType, EnumOfficialJobPublishStatus } from '@src/types';
import { array2String } from '@utils/formatData';
import classNames from 'classnames';
import { POSITION_STATE } from '@src/enums';
import { isUrl } from '@utils/tools';
import './campusApplyRecord.scss';

const { Step } = Steps;
const TimelineItem = Timeline.Item;
interface IProps {
  volunteerSchedule: IVolunteerRecord;
}
const VOLUNTEER_TYPE = {
  0: '志愿',
  1: '志愿一',
  2: '志愿二',
  3: '志愿三',
  4: '调剂职位',
};

const CampusApplyRecord: FC<IProps> = ({ volunteerSchedule }) => {
  const { projectName, volunteerItemSchedule, applyProcess, externalShowProgress } = volunteerSchedule || {};
  const { go } = useGo();
  const [isFold, setIsFold] = useState<boolean>(true);
  // 处于进行中的step的index;
  let isInProcessIndex = -1;
  if (Array.isArray(applyProcess)) {
    const index = [...applyProcess].reverse().findIndex((item) => item.existStep === true);
    if (index !== -1) {
      isInProcessIndex = applyProcess.length - 1 - index;
    }
  }
  const renderDescriptionContent = useCallback((info) => {
    if (!info) return null;
    const tempArr: JSX.Element[] = [];
    for (const key in info) {
      if (Object.prototype.hasOwnProperty.call(info, key)) {
        const element = info[key];
        if (key !== '笔试时间' && key !== '笔试日期') {
          tempArr.push(
            <div key={key}>
              <span>{key}：</span>
              {isUrl(element) ? <a href={element}>{element}</a> : <span>{element}</span>}
            </div>,
          );
        }
      }
    }
    return <div>{tempArr}</div>;
  }, []);

  const renderDescription = useCallback((list) => {
    if (!list || !Array.isArray(list)) return null;
    return (
      <Timeline className="base-demo">
        {list.map((item, index) => (
          <TimelineItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            title={<div>{item.title}</div>}
            content={renderDescriptionContent(item.logMap) as any}
          />
        ))}
      </Timeline>
    );
  }, []);

  const renderProjectStatus = (_externalShowProgress) => {
    if (_externalShowProgress === null) {
      return null;
    }
    if (_externalShowProgress) {
      return <span className="zp-status-on">进行中</span>;
    }
    return (
      <Tooltip message="已结束校招项目的投递状态不影响进行中的校招项目流程" autoDestory>
        <span className="zp-status-off">
          已结束
          <Icon type="info-circle-o" style={{ paddingLeft: '2px', color: 'rgba(0,0,0,0.5)' }} />
        </span>
      </Tooltip>
    );
  };

  return (
    <div className="com_campusApplyRecord">
      <div className="projectNameWrap">
        <span
          className="projectName cursor_pointer"
          onClick={() => {
            if (!Array.isArray(volunteerItemSchedule)) {
              message.error({
                message: '无志愿记录',
                duration: 2000,
              });
              return;
            }
            const jobInfo = volunteerItemSchedule.find(
              (item) => item.jobId && item.officialJobPublishStatus !== EnumOfficialJobPublishStatus.notPublish,
            );
            if (!jobInfo) {
              message.error({
                message: '无有效职位',
                duration: 2000,
              });
              return;
            }
            go('/delivery-confirm', {
              jobUnionId: jobInfo.jobId,
              jobShareType: '1',
              highlightType: 'campus',
            });
          }}
        >
          {projectName}
        </span>
        {renderProjectStatus(externalShowProgress)}
      </div>
      {Array.isArray(volunteerItemSchedule) &&
        volunteerItemSchedule.map((item, index) => {
          const {
            volunteerItemId,
            jobType,
            jobId,
            jobName,
            volunteerType,
            workCity,
            aspirationDirectionNameList,
            volunteerItemStatus,
            volunteerItemStepName,
            deliverTime,
            bgName,
            officialJobPublishStatus,
          } = item;
          return (
            <div className="volunteerItemSchedule" key={volunteerItemId || index}>
              <div>
                <div className="jobName">
                  <span>{VOLUNTEER_TYPE[volunteerType]}</span>：
                  {volunteerType === EnumVolunteerType.notHaveIntentionJob ? (
                    '无明确意向'
                  ) : (
                    <>
                      <span
                        className="title hidden-ellipsis cursor_pointer"
                        onClick={() => {
                          if (officialJobPublishStatus === EnumOfficialJobPublishStatus.notPublish) {
                            message.error({
                              message: '该职位未在官网发布过',
                              duration: 2000,
                            });
                            return;
                          }
                          go(
                            '/position/detail',
                            {
                              jobUnionId: jobId,
                              jobShareType: '1',
                              highlightType: 'campus',
                            },
                            {
                              isNeedFormatUrl: true,
                            },
                          );
                        }}
                      >
                        {jobName}
                      </span>
                      <span className="tag green">{POSITION_STATE[jobType]}</span>
                    </>
                  )}
                </div>
                <div className="detailWrap">
                  <div className="detailInfo workPlace">
                    <Icon type="location-o" style={{ fontSize: 14, marginRight: 3 }} />
                    <span>意向工作地：</span>
                    <span>{array2String(workCity, 2)}</span>
                  </div>
                  <div className="line" />
                  {!!aspirationDirectionNameList?.length && (
                    <>
                      <div className="detailInfo direction">
                        <Icon type="briefcase" style={{ fontSize: 14, marginRight: 3 }} />
                        <span>意向方向：{array2String(aspirationDirectionNameList, 2)}</span>
                      </div>
                      <div className="line" />
                    </>
                  )}
                  {bgName && (
                    <>
                      <div className="detailInfo bgName">
                        <Tooltip message={`意向部门：${bgName}`} autoDestory>
                          <Icon type="hierarchy-o" style={{ fontSize: 14, marginRight: 3 }} />
                          <span>意向部门：{bgName}</span>
                        </Tooltip>
                      </div>
                      <div className="line" />
                    </>
                  )}
                  {/* {deliverType && (
                    <>
                      <div className="detailInfo applyType">
                        <Icon type="file-export" style={{ fontSize: 16, marginRight: 4 }} />
                        <span>投递方式：{deliverType}</span>
                      </div>
                      <div className="line" />
                    </>
                  )} */}
                  {dayjs(deliverTime).isValid() && (
                    <div className="detailInfo time">
                      <Icon type="time-o" style={{ fontSize: 14, marginRight: 3 }} />
                      <span>投递时间：{dayjs(deliverTime).format('YYYY/MM/DD HH:mm:ss')}</span>
                    </div>
                  )}
                </div>
              </div>
              <div
                className={classNames('statusWrap ', {
                  status0: volunteerItemStatus === EnumVolunteerItemStatus.notStart,
                  status1: volunteerItemStatus === EnumVolunteerItemStatus.inProcess,
                  status2: volunteerItemStatus === EnumVolunteerItemStatus.end,
                })}
              >
                <div className="statusText">
                  {volunteerItemStepName === '笔试' && (
                    <img src={require('@/imgs/note.png')} alt="" style={{ width: 15, height: 15, marginRight: 4 }} />
                  )}
                  {volunteerItemStepName !== '笔试' && (
                    <Icon
                      type={classNames({
                        'time-o': volunteerItemStatus === EnumVolunteerItemStatus.notStart,
                        'info-circle-o':
                          volunteerItemStatus === EnumVolunteerItemStatus.inProcess ||
                          volunteerItemStatus === EnumVolunteerItemStatus.end,
                      })}
                      style={{ fontSize: 16, marginRight: 4 }}
                    />
                  )}
                  <span>{volunteerItemStepName}</span>
                </div>
              </div>
            </div>
          );
        })}
      <div
        className="foldWrap cursor_pointer"
        onClick={() => {
          setIsFold(!isFold);
        }}
      >
        <span>志愿状态说明</span>
        <span className={classNames('iconWrap', { iconWrap180: !isFold })}>
          <Icon type="triangle-down" style={{ fontSize: 24 }} />
        </span>
      </div>
      {Array.isArray(applyProcess) && (
        <div className={classNames('applyProcessWrap', { hide: isFold })}>
          <Steps size="small">
            {applyProcess.map((item, index) => (
              <Step
                key={item.stepName}
                // eslint-disable-next-line no-nested-ternary
                status={isInProcessIndex === index ? 'process' : item.existStep ? 'finish' : 'wait'}
                title={item.stepName}
                description={renderDescription(item.stepInfo)}
              />
            ))}
          </Steps>
        </div>
      )}
    </div>
  );
};

export default memo(CampusApplyRecord);
